import { useTranslation } from 'react-i18next';
import { ActionIcon, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconDownload, IconFileText } from '@tabler/icons-react';
import SignatureBadge from '@/components/app/deals/SignatureBadge';
import { useDownloadDocument } from '@/hooks/useDeals';
import { signatureStateView } from '@/lib/dealStatus';
import { forwardingDocLabel } from '@/lib/forwardingView';
import type { BargainDocument, SignatureRole } from '@/types/deals';

/** Скачивание строки: свой источник данных подставляет своё (перевозчик — trucking-контур). */
export interface DocDownloadControl {
  readonly download: (documentId: number) => void;
  /** Идёт ли скачивание ИМЕННО этого документа — предикат, чтобы параллельные загрузки не «перебивали»
   *  индикатор соседних строк. */
  readonly isDownloading: (documentId: number) => boolean;
}

interface DocRowProps {
  readonly doc: BargainDocument;
  /** Показывать бейджи подписей (для УПД/документов, требующих подписи). */
  readonly withSignatures?: boolean;
  /**
   * Чем скачивать документ. По умолчанию — документы сделки (order-service). Экраны с другим
   * источником (карточка заявки перевозчика) передают своё: там другой эндпоинт и своя авторизация.
   */
  readonly downloadControl?: DocDownloadControl;
}

const SIGN_ROLES: readonly SignatureRole[] = ['SELLER', 'BUYER', 'AGENT', 'FORWARDER', 'BANK'];

// Строка документа перевозки: контекстный лейбл (УПД за дату / Реестр принятого / …) + (опц.) подписи
// ролей + скачивание. Read-only.
export default function DocRow({ doc, withSignatures = false, downloadControl }: DocRowProps) {
  const { t } = useTranslation();
  // Хук сделки вызываем всегда (правила хуков), но при своём источнике его результат не используем.
  const dealDownload = useDownloadDocument();
  const startDownload = downloadControl?.download ?? dealDownload.mutate;
  const downloading = downloadControl
    ? doc.id != null && downloadControl.isDownloading(doc.id)
    : dealDownload.isPending && dealDownload.variables === doc.id;
  const signedRoles = withSignatures
    ? SIGN_ROLES.filter((role) => signatureStateView(doc.signatures?.[role]?.state) !== null)
    : [];

  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
      <Group gap="xs" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
        <ThemeIcon size={28} radius="md" variant="light" color="gray">
          <IconFileText size={15} />
        </ThemeIcon>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text fz="sm" lineClamp={2}>
            {forwardingDocLabel(doc, t)}
          </Text>
          {signedRoles.length > 0 ? (
            <Group gap={4} wrap="wrap">
              {signedRoles.map((role) => (
                <SignatureBadge key={role} role={role} info={doc.signatures?.[role]} />
              ))}
            </Group>
          ) : null}
        </Stack>
      </Group>
      {doc.id != null ? (
        <Tooltip label={t('app.deals.docs.download')}>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            aria-label={t('app.deals.docs.download')}
            loading={downloading}
            onClick={() => startDownload(doc.id!)}
          >
            <IconDownload size={15} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
}
